// Authentication state hook.
//
// Resolves three things on mount and re-fetches on demand:
//   * /oauth/me      → curator/admin role flags + connection id
//   * /api/org/status → connected org name + instance host
//   * /api/curator/me → optional Q Branch curator session (email/password)
//
// Exposes:
//   * connect(loginUrl?) — redirects to /oauth/connect (full page reload)
//   * disconnect() — POST /oauth/disconnect, then re-fetches
//   * refetch() — re-runs both reads (used after curator login)

import { useCallback, useEffect, useState } from "react";
import { apiSoft } from "@/lib/api/http";

export interface OrgStatus {
  connected: boolean;
  org_id?: string;
  org_name?: string;
  instance_url?: string;
  user_email?: string | null;
  user_name?: string | null;
}

export interface AuthMe {
  connected: boolean;
  org_connection_id?: string | null;
  is_curator?: boolean;
  is_admin?: boolean;
  user_email?: string | null;
  user_name?: string | null;
}

export interface CuratorMe {
  logged_in: boolean;
  email?: string;
  name?: string;
}

export interface AuthState {
  loading: boolean;
  // True when EITHER an org session OR a curator cookie is active.
  authenticated: boolean;
  // True only when an actual Salesforce org connection is alive.
  orgConnected: boolean;
  isAdmin: boolean;
  isCurator: boolean;
  // Display name + email of the logged-in user (from SF org or curator
  // record). `null` until auth resolves.
  userName: string | null;
  userEmail: string | null;
  org: OrgStatus | null;
  me: AuthMe | null;
  curator: CuratorMe | null;
  refetch: () => Promise<void>;
  connect: (loginUrl?: string) => void;
  disconnect: () => Promise<void>;
  curatorLogout: () => Promise<void>;
}

let cached: {
  org: OrgStatus | null;
  me: AuthMe | null;
  curator: CuratorMe | null;
  fetchedAt: number;
} | null = null;

const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((fn) => fn());
}

// F-H2 (2026-04-20): single-flight guard. Every component that calls
// useAuth() spins up a useEffect; before this guard, mounting N
// auth-aware components on first paint fired N parallel `loadAll`
// triple-fetches. With a typical V2 page that's 6-12 redundant trips
// to /oauth/me, /api/org/status, and /api/curator/me. We coalesce
// concurrent callers onto a single promise — second/third callers
// just await the in-flight load.
let inFlight: Promise<void> | null = null;

async function loadAll(): Promise<void> {
  if (inFlight) {
    return inFlight;
  }
  inFlight = (async () => {
    try {
      const [org, me, curator] = await Promise.all([
        apiSoft<OrgStatus>("/api/org/status", { silentAuth: true }),
        apiSoft<AuthMe>("/oauth/me", { silentAuth: true }),
        apiSoft<CuratorMe>("/api/curator/me", { silentAuth: true }),
      ]);
      cached = {
        org: org ?? { connected: false },
        me: me ?? { connected: false },
        curator: curator ?? { logged_in: false },
        fetchedAt: Date.now(),
      };
      notify();
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export function useAuth(): AuthState {
  const [, force] = useState(0);

  useEffect(() => {
    const trigger = (): void => force((n) => n + 1);
    subscribers.add(trigger);
    if (!cached) {
      void loadAll();
    }
    return () => {
      subscribers.delete(trigger);
    };
  }, []);

  const refetch = useCallback(async () => {
    await loadAll();
  }, []);

  const connect = useCallback((loginUrl?: string) => {
    const params = new URLSearchParams();
    if (loginUrl) params.set("login_url", loginUrl);
    // Bring the user back to wherever they were inside the V2 app.
    // Skip the bounce-back if they're on a V1 archive page (/v1/*) or
    // a backend-owned path (/api, /oauth, /ws) — those aren't real
    // SPA routes and would error after OAuth callback.
    const here = `${window.location.pathname}${window.location.search}`;
    const isAppRoute =
      !here.startsWith("/v1") &&
      !here.startsWith("/api") &&
      !here.startsWith("/oauth") &&
      !here.startsWith("/ws");
    const next = isAppRoute ? here : "/";
    params.set("next", next);
    window.location.href = `/oauth/connect?${params.toString()}`;
  }, []);

  const disconnect = useCallback(async () => {
    await apiSoft("/oauth/disconnect", { method: "POST" });
    await loadAll();
  }, []);

  const curatorLogout = useCallback(async () => {
    await apiSoft("/api/curator/logout", { method: "POST" });
    await loadAll();
  }, []);

  const loading = cached === null;
  const org = cached?.org ?? null;
  const me = cached?.me ?? null;
  const curator = cached?.curator ?? null;
  const orgConnected = !!org?.connected;
  const curatorLoggedIn = !!curator?.logged_in;
  const authenticated = orgConnected || curatorLoggedIn;
  const isAdmin = !!me?.is_admin;
  const isCurator = !!me?.is_curator || isAdmin;
  const userEmail =
    me?.user_email ?? org?.user_email ?? curator?.email ?? null;
  const userName =
    me?.user_name ?? org?.user_name ?? curator?.name ?? null;

  return {
    loading,
    authenticated,
    orgConnected,
    isAdmin,
    isCurator,
    userName,
    userEmail,
    org,
    me,
    curator,
    refetch,
    connect,
    disconnect,
    curatorLogout,
  };
}
